import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import _get from 'lodash/get';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  FilterGroupingEnum,
  InterventionScopeNameEnum,
  User,
  UserFilterInputObject,
  UserRoleEnum,
} from 'graphql-common';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import Button from '@lib/components/Button/Button';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import UserModalForm from 'components/UserModalForm/UserModalForm';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import useSearchParam from '@lib/hooks/useSearchParam';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import useUsersQueryHook from 'utils/fetch/hooks/useUsersQueryHook';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import CompanyPlanBanner from '@lib/components/CompanyPlanBanner/CompanyPlanBanner';
import useWhoamiQueryHook from 'utils/fetch/hooks/useWhoamiQueryHook';
import { IconName } from '@lib/components/Modal/enums';
import {
  calculateActiveUsersPercentage,
  getPlanState,
} from '@lib/utils/accessesManagement';
import { PlanState } from '@lib/enums/plan';
import TabBar from '@lib/components/TabBar/TabBar';
import useTabParam from '@lib/hooks/useTabParam';
import getUsersTabs from 'routes/Users/utils/getUsersTabs';
import { TabBarItem as TabBarItemType } from '@lib/components/TabBar/constants';
import { UsersViewMode } from 'routes/Users/enums';
import getUsersTableActionsForSelected from './utils/getUsersTableActionsForSelected';
import getUsersTableColumns from './utils/getUsersTableColumns';
import getUsersTableActionsMenu from './utils/getUsersTableActionsMenu';
import useUsersActions from './utils/useUsersActions';

const modals = [
  Modals.UserFormModal,
  Modals.UserDeleteModal,
  Modals.MoveMembersToTeamModal,
];

function UsersContainer() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [searchQuery] = useSearchParam();
  const [paginationParams] = usePaginationParams();
  const [tabParam = InterventionScopeNameEnum.Opened, setTabParam] =
    useTabParam({ defaultTab: InterventionScopeNameEnum.Opened });
  const onTabChange = (item?: TabBarItemType) =>
    item?.value ? setTabParam(item.value) : undefined;

  const { currentUser, refetch: refetchWhoami } = useWhoamiQueryHook({
    includeFullData: false,
  });
  const activeUsersCount = _get(currentUser, 'company.activeUsersCount');
  const usersLimit = _get(currentUser, 'company.plan.usersLimit');
  const activeUsersPercentage = calculateActiveUsersPercentage(
    activeUsersCount || 0,
    usersLimit,
  );
  const planState = getPlanState(activeUsersPercentage);

  const { getUserAccessByComponent, handleOpenPlanModal }: ContextProps =
    useOutletContext();
  const isUserCreateAvailable = planState !== PlanState.Attention;

  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.UsersManagement) ===
    AccessLevelEnum.Read;

  const additionalFilters: UserFilterInputObject[] = [];
  if (tabParam === UsersViewMode.FullAdmins) {
    additionalFilters.push({
      role: {
        grouping: FilterGroupingEnum.Or,
        predicate: { eq: UserRoleEnum.FullAdmin },
      },
    });
  } else if (tabParam === UsersViewMode.Users) {
    additionalFilters.push({
      role: {
        grouping: FilterGroupingEnum.Or,
        predicate: { eq: UserRoleEnum.User },
      },
    });
  } else if (tabParam === UsersViewMode.Unassigned) {
    additionalFilters.push(
      {
        role: {
          grouping: FilterGroupingEnum.And,
          predicate: { eq: UserRoleEnum.User },
        },
      },
      {
        siteMembershipsId: {
          grouping: FilterGroupingEnum.And,
          predicate: { null: true },
        },
      },
      {
        sectorMembershipsId: {
          grouping: FilterGroupingEnum.And,
          predicate: { null: true },
        },
      },
    );
  }
  const useUsersQueryHookResult = useUsersQueryHook({
    searchQuery,
    paginationParams,
    includeFullData: true,
    additionalFilters,
    skipGetCounters: false,
  });

  const useBulkSelectResult = useTableItemsBulkSelect<User>();
  const useModalManagementResult = useModalManagement(modals, () =>
    useBulkSelectResult.handleSelectSingleTableItem(null),
  );
  const { isModalOpened, openModal, closeModal } = useModalManagementResult;
  const {
    currentUserId,
    destroyLoading,
    onActivate,
    onBulkActivate,
    onBulkDeactivate,
    onConfirmedDelete,
    onConfirmedTeamMembersUpdate,
    onDeactivate,
    onDelete,
    onEdit,
    onEditAccesses,
    onResendEmailConfirmationHandler,
    onResendRegistrationConfirmationHandler,
    onSubmitUserFormCallback,
    onTeamMembersUpdate,
    onView,
    openSectorPage,
    openSitePage,
    openTeamPage,
    selectedUserId,
    teamsLoadOptions,
  } = useUsersActions({
    refetchWhoami,
    useUsersQueryHookResult,
    useModalManagementResult,
    useBulkSelectResult,
  });

  const tabs = useMemo(
    () =>
      getUsersTabs({
        t,
        counters: useUsersQueryHookResult.counters,
      }),
    [t, useUsersQueryHookResult.counters],
  );
  const columns = useMemo(
    () =>
      getUsersTableColumns({
        currentUserId,
        isViewOnly,
        onResendEmailConfirmationHandler,
        onResendRegistrationConfirmationHandler,
        onViewSectorHandler: openSectorPage,
        onViewSiteHandler: openSitePage,
        onViewTeamHandler: openTeamPage,
        t,
      }),
    [
      currentUserId,
      isViewOnly,
      onResendEmailConfirmationHandler,
      onResendRegistrationConfirmationHandler,
      openSectorPage,
      openSitePage,
      openTeamPage,
      t,
    ],
  );
  const tableActionsMenu = useMemo(
    () =>
      getUsersTableActionsMenu({
        currentUserId,
        isViewOnly,
        onActivate,
        onDeactivate,
        onDelete,
        onEdit,
        onEditAccesses,
        onView,
        t,
      }),
    [
      currentUserId,
      isViewOnly,
      onActivate,
      onDeactivate,
      onDelete,
      onEdit,
      onEditAccesses,
      onView,
      t,
    ],
  );
  const tableActionsForSelected = useMemo(
    () =>
      getUsersTableActionsForSelected({
        teamsLoadOptions,
        onBulkActivate,
        onBulkDeactivate,
        onTeamMembersUpdate,
        t,
        selectedItemsCount: useBulkSelectResult.getSelectedItemsCount(
          useUsersQueryHookResult.totalCount,
        ),
        isViewOnly,
      }),
    [
      teamsLoadOptions,
      onBulkActivate,
      onBulkDeactivate,
      onTeamMembersUpdate,
      t,
      useBulkSelectResult,
      useUsersQueryHookResult.totalCount,
      isViewOnly,
    ],
  );

  return (
    <>
      <BreadCrumbs data={getBreadCrumbsDataByKey({ pathname, t })} />
      {useUsersQueryHookResult.error ? (
        <ErrorPage
          status={getStatusFromError(useUsersQueryHookResult.error)}
          description={getDescriptionFromError(useUsersQueryHookResult.error)}
        />
      ) : (
        <>
          {isViewOnly ? null : (
            <CompanyPlanBanner
              activeUsersCount={activeUsersCount}
              planState={planState}
              handleOpenPlanModal={handleOpenPlanModal}
              usersLimit={usersLimit}
              activeUsersPercentage={activeUsersPercentage}
            />
          )}
          <PageHeader title={t('team-members')}>
            {isViewOnly ? (
              <ViewOnlyChip />
            ) : (
              <Button
                buttonText={t('add-team-member')}
                data-test-id="users__add-button"
                leftIcon={isUserCreateAvailable ? 'add' : 'lock'}
                onClick={() => openModal(Modals.UserFormModal)}
                disabled={!isUserCreateAvailable}
              />
            )}
          </PageHeader>
          {useUsersQueryHookResult.firstLoading ? (
            <CircleLoader />
          ) : (
            <PageBody>
              <TabBar
                items={tabs}
                active={tabParam}
                onActiveChange={onTabChange}
                useSidePaddings
                bleedSidePaddings
              />
              <Search
                placeholder={t('users-search-placeholder')}
                data-test-id="users__search-input"
              />
              <Table
                actionsMenu={tableActionsMenu}
                actionsForSelected={tableActionsForSelected}
                columns={columns}
                counterText={t('team-member-plural', {
                  count: useUsersQueryHookResult.totalCount,
                })}
                data={useUsersQueryHookResult.collection}
                // fullHeight
                id="users"
                loading={useUsersQueryHookResult.loading}
                totalEntries={useUsersQueryHookResult.totalCount}
                getIsRowDisabled={({
                  passwordSetupRequired,
                  verified,
                  deactivatedAt,
                }) => passwordSetupRequired || !verified || !!deactivatedAt}
                useBulkSelectResult={useBulkSelectResult}
              />
            </PageBody>
          )}
          <UserModalForm
            isOpen={isModalOpened(Modals.UserFormModal)}
            onClose={() => {
              closeModal(Modals.UserFormModal);
              useBulkSelectResult.handleSelectSingleTableItem(null);
            }}
            onSubmitUserFormCallback={onSubmitUserFormCallback}
            selectedUserId={selectedUserId}
            title={
              selectedUserId ? t('edit-team-member') : t('add-team-member')
            }
          />
          <ConfirmationModal
            confirmButtonLabel={t('yes-delete')}
            description={t('delete-user-text')}
            icon={IconName.Delete}
            isOpen={isModalOpened(Modals.UserDeleteModal)}
            loading={destroyLoading}
            onClose={() => closeModal(Modals.UserDeleteModal)}
            onConfirm={onConfirmedDelete}
            title={t('are-you-sure')}
            useProgress
          />
          <ConfirmationModal
            confirmButtonLabel={t('yes-move-users')}
            description={t('move-users--to-team-text')}
            icon={IconName.Delete}
            isOpen={isModalOpened(Modals.MoveMembersToTeamModal)}
            onClose={() => closeModal(Modals.MoveMembersToTeamModal)}
            onConfirm={onConfirmedTeamMembersUpdate}
            title={t('are-you-sure')}
          />
        </>
      )}
    </>
  );
}

export default UsersContainer;
